import {
    type ReactNode,
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    formatDateTime,
    formatCurrency,
    Badge,
    Table
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import type {
    ITableRowProps
} from "ncore-ui-kit";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    DemoFrame,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "Table";

const USAGE_CODE = `const [selectedId, setSelectedId] = useState("mercan");

<Table
    caption="Invoices"
    isRaised={true}
>
    <Table.Header>
        <Table.Row>
            <Table.Head>Customer</Table.Head>
            <Table.Head isNumeric={true}>Amount</Table.Head>
        </Table.Row>
    </Table.Header>
    <Table.Body>
        <Table.Row
            isSelected={selectedId === "mercan"}
            onPress={() => setSelectedId("mercan")}
        >
            <Table.Cell>Mercan Software</Table.Cell>
            <Table.Cell isNumeric={true}>$4,820</Table.Cell>
        </Table.Row>
    </Table.Body>
    <Table.Footer>
        <Table.Row>
            <Table.Cell>Total</Table.Cell>
            <Table.Cell isNumeric={true}>$4,820</Table.Cell>
        </Table.Row>
    </Table.Footer>
</Table>`;

const SCROLL_CODE = `const {
    shapeConfigs
} = NCoreUIKitTheme.useContext();

<Table
    isFirstColumnSticky={true}
    isHeaderSticky={true}
    style={{
        maxHeight: shapeConfigs.chartHeight.medium
    }}
    caption="Invoices"
>
    ...
</Table>`;

const SIZES = [
    "small",
    "medium",
    "large"
] as const;

type InvoiceType = {
    tone: "success" | "warning" | "danger";
    status: string;
    amount: number;
    name: string;
    id: string;
    due: Date;
};

const getInvoices = (t: DocsTranslate): Array<InvoiceType> => [
    {
        status: t("tablePage.statusPaid"),
        name: t("tablePage.customer1"),
        due: new Date(2026, 10, 3),
        tone: "success",
        id: "invoice1",
        amount: 4820
    },
    {
        status: t("tablePage.statusPending"),
        name: t("tablePage.customer2"),
        due: new Date(2026, 10, 12),
        tone: "warning",
        id: "invoice2",
        amount: 1260
    },
    {
        status: t("tablePage.statusOverdue"),
        name: t("tablePage.customer3"),
        due: new Date(2026, 10, 20),
        id: "invoice3",
        tone: "danger",
        amount: 3150
    }
];

const getMoreInvoices = (t: DocsTranslate): Array<InvoiceType> => [
    {
        status: t("tablePage.statusPaid"),
        name: t("tablePage.customer4"),
        due: new Date(2026, 10, 24),
        tone: "success",
        id: "invoice4",
        amount: 2340
    },
    {
        status: t("tablePage.statusPending"),
        name: t("tablePage.customer5"),
        due: new Date(2026, 10, 27),
        tone: "warning",
        id: "invoice5",
        amount: 980
    },
    {
        status: t("tablePage.statusPaid"),
        name: t("tablePage.customer6"),
        due: new Date(2026, 11, 2),
        tone: "success",
        id: "invoice6",
        amount: 5410
    }
];

const CURRENCIES = {
    tr: "TRY",
    en: "USD"
} as const;

const FRAME_STYLE = {
    width: "100%"
};

const TableFrame = ({
    isNarrow = false,
    children
}: {
    children: ReactNode;
    isNarrow?: boolean;
}) => {
    const {
        shapeConfigs
    } = NCoreUIKitTheme.useContext();

    return <div
        style={{
            ...FRAME_STYLE,
            maxWidth: isNarrow ? shapeConfigs.dialogMaxWidth : shapeConfigs.sheetMaxWidth
        }}
    >
        <DemoFrame>
            {children}
        </DemoFrame>
    </div>;
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("section.sizes"),
        id: "sizes"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("tablePage.columnsTitle"),
        id: "columns"
    },
    {
        title: t("tablePage.scrollTitle"),
        id: "scroll"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const TablePage = () => {
    const docsTokens = NCoreUIKitTheme.useContext();

    const {
        language,
        t
    } = useDocsI18n();

    const {
        activeLocale
    } = NCoreUIKitLocalize.useContext();

    const [
        selectedId,
        setSelectedId
    ] = useState("invoice2");

    const invoices = getInvoices(t);

    const scrollInvoices = [
        ...invoices,
        ...getMoreInvoices(t)
    ];

    const total = invoices.reduce((sum, invoice) => sum + invoice.amount, 0);

    const formatAmount = (value: number) => formatCurrency({
        currency: CURRENCIES[language],
        options: {
            maximumFractionDigits: 0
        },
        locale: activeLocale,
        value
    });

    const renderHeader = () => <Table.Header>
        <Table.Row>
            <Table.Head>
                {t("tablePage.columnCustomer")}
            </Table.Head>
            <Table.Head>
                {t("sample.columnStatus")}
            </Table.Head>
            <Table.Head
                isNumeric={true}
            >
                {t("sample.columnAmount")}
            </Table.Head>
        </Table.Row>
    </Table.Header>;

    const renderRow = (invoice: InvoiceType, props: ITableRowProps = {}) => <Table.Row
        {...props}
        key={invoice.id}
    >
        <Table.Cell>
            {invoice.name}
        </Table.Cell>
        <Table.Cell>
            {invoice.status}
        </Table.Cell>
        <Table.Cell
            isNumeric={true}
        >
            {formatAmount(invoice.amount)}
        </Table.Cell>
    </Table.Row>;

    const renderStateRow = (title: string, props: ITableRowProps) => <Table.Row
        {...props}
    >
        <Table.Cell>
            {title}
        </Table.Cell>
        <Table.Cell>
            {invoices[0]?.name}
        </Table.Cell>
        <Table.Cell
            isNumeric={true}
        >
            {formatAmount(invoices[0]?.amount ?? 0)}
        </Table.Cell>
    </Table.Row>;

    return <Article>
        <ComponentHeader
            lead={t("tablePage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("tablePage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("tablePage.overviewCaption")}
            >
                <TableFrame>
                    <Table
                        caption={t("tablePage.caption")}
                        isCaptionHidden={true}
                        isRaised={true}
                    >
                        {renderHeader()}
                        <Table.Body>
                            {
                                invoices.map((invoice) => renderRow(invoice, {
                                    onPress: () => setSelectedId(invoice.id),
                                    isSelected: selectedId === invoice.id
                                }))
                            }
                        </Table.Body>
                    </Table>
                </TableFrame>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("section.sizes")}
            id="sizes"
        >
            <Paragraph>
                {t("tablePage.sizesText")}
            </Paragraph>
            <Preview>
                {
                    SIZES.map((size) => <TableFrame
                        key={size}
                    >
                        <Table
                            isRaised={true}
                            caption={size}
                            size={size}
                        >
                            {renderHeader()}
                            <Table.Body>
                                {invoices.slice(0, 2).map((invoice) => renderRow(invoice))}
                            </Table.Body>
                        </Table>
                    </TableFrame>)
                }
            </Preview>
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("tablePage.statesText")}
            </Paragraph>
            <Preview>
                <TableFrame>
                    <Table
                        caption={t("tablePage.caption")}
                        isCaptionHidden={true}
                        isRaised={true}
                    >
                        {renderHeader()}
                        <Table.Body>
                            {renderStateRow(t("sample.stateDefault"), {})}
                            {
                                renderStateRow(t("tablePage.statePressable"), {
                                    onPress: () => undefined
                                })
                            }
                            {
                                renderStateRow(t("tablePage.stateSelected"), {
                                    onPress: () => undefined,
                                    isSelected: true
                                })
                            }
                            {
                                renderStateRow(t("tablePage.stateDisabled"), {
                                    isDisabled: true
                                })
                            }
                        </Table.Body>
                    </Table>
                </TableFrame>
            </Preview>
        </Section>
        <Section
            title={t("tablePage.columnsTitle")}
            id="columns"
        >
            <Paragraph>
                {t("tablePage.columnsText")}
            </Paragraph>
            <Preview>
                <TableFrame>
                    <Table
                        caption={t("tablePage.caption")}
                        isRaised={true}
                    >
                        <Table.Header>
                            <Table.Row>
                                <Table.Head>
                                    {t("tablePage.columnCustomer")}
                                </Table.Head>
                                <Table.Head
                                    align="center"
                                >
                                    {t("sample.columnStatus")}
                                </Table.Head>
                                <Table.Head
                                    isNumeric={true}
                                >
                                    {t("sample.columnAmount")}
                                </Table.Head>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {
                                invoices.map((invoice) => <Table.Row
                                    key={invoice.id}
                                >
                                    <Table.Cell>
                                        {invoice.name}
                                    </Table.Cell>
                                    <Table.Cell
                                        align="center"
                                    >
                                        <Badge
                                            title={invoice.status}
                                            spreadBehaviour="free"
                                            type={invoice.tone}
                                            size="small"
                                        />
                                    </Table.Cell>
                                    <Table.Cell
                                        isNumeric={true}
                                    >
                                        {formatAmount(invoice.amount)}
                                    </Table.Cell>
                                </Table.Row>)
                            }
                        </Table.Body>
                        <Table.Footer>
                            <Table.Row>
                                <Table.Cell
                                    colSpan={2}
                                >
                                    {t("tablePage.total")}
                                </Table.Cell>
                                <Table.Cell
                                    isNumeric={true}
                                >
                                    {formatAmount(total)}
                                </Table.Cell>
                            </Table.Row>
                        </Table.Footer>
                    </Table>
                </TableFrame>
            </Preview>
        </Section>
        <Section
            title={t("tablePage.scrollTitle")}
            id="scroll"
        >
            <Paragraph>
                {t("tablePage.scrollText")}
            </Paragraph>
            <Preview
                caption={t("tablePage.scrollCaption")}
            >
                <TableFrame
                    isNarrow={true}
                >
                    <Table
                        style={{
                            maxHeight: docsTokens.shapeConfigs.chartHeight.medium
                        }}
                        caption={t("tablePage.caption")}
                        isFirstColumnSticky={true}
                        isCaptionHidden={true}
                        isHeaderSticky={true}
                        isRaised={true}
                    >
                        <Table.Header>
                            <Table.Row>
                                <Table.Head>
                                    {t("tablePage.columnCustomer")}
                                </Table.Head>
                                <Table.Head>
                                    {t("sample.columnStatus")}
                                </Table.Head>
                                <Table.Head>
                                    {t("tablePage.columnDue")}
                                </Table.Head>
                                <Table.Head>
                                    {t("tablePage.columnNote")}
                                </Table.Head>
                                <Table.Head
                                    isNumeric={true}
                                >
                                    {t("sample.columnAmount")}
                                </Table.Head>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {
                                scrollInvoices.map((invoice) => <Table.Row
                                    key={invoice.id}
                                >
                                    <Table.Cell>
                                        {invoice.name}
                                    </Table.Cell>
                                    <Table.Cell>
                                        {invoice.status}
                                    </Table.Cell>
                                    <Table.Cell>
                                        {
                                            formatDateTime({
                                                locale: activeLocale,
                                                date: invoice.due
                                            })
                                        }
                                    </Table.Cell>
                                    <Table.Cell>
                                        {t("tablePage.noteText")}
                                    </Table.Cell>
                                    <Table.Cell
                                        isNumeric={true}
                                    >
                                        {formatAmount(invoice.amount)}
                                    </Table.Cell>
                                </Table.Row>)
                            }
                        </Table.Body>
                    </Table>
                </TableFrame>
            </Preview>
            <CodeBlock
                code={SCROLL_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("tablePage.a11yRoles"),
                    t("tablePage.a11yCaption"),
                    t("tablePage.a11ySort"),
                    t("tablePage.a11yRow"),
                    t("tablePage.a11yFocus"),
                    t("tablePage.a11yDisabled"),
                    t("tablePage.a11yContext")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("tablePage.propCaption"),
                        name: "caption",
                        type: "string"
                    },
                    {
                        description: t("tablePage.propIsCaptionHidden"),
                        name: "isCaptionHidden",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        type: "\"small\" | \"medium\" | \"large\"",
                        description: t("tablePage.propSize"),
                        defaultValue: "\"medium\"",
                        name: "size"
                    },
                    {
                        description: t("tablePage.propIsRaised"),
                        defaultValue: "false",
                        name: "isRaised",
                        type: "boolean"
                    },
                    {
                        description: t("tablePage.propIsHeaderSticky"),
                        name: "isHeaderSticky",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("tablePage.propIsFirstColumnSticky"),
                        name: "isFirstColumnSticky",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("tablePage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("tablePage.propChildren"),
                        type: "ReactNode",
                        name: "children"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("tablePage.propsNote")}
            </Paragraph>
            <Paragraph>
                {t("tablePage.rowTitle")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("tablePage.rowOnPress"),
                        type: "() => void",
                        name: "onPress"
                    },
                    {
                        description: t("tablePage.rowIsSelected"),
                        defaultValue: "false",
                        name: "isSelected",
                        type: "boolean"
                    },
                    {
                        description: t("tablePage.rowIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("tablePage.rowCustomTheme"),
                        name: "customTheme"
                    }
                ]}
            />
            <Paragraph>
                {t("tablePage.cellTitle")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("tablePage.cellIsNumeric"),
                        defaultValue: "false",
                        name: "isNumeric",
                        type: "boolean"
                    },
                    {
                        type: "\"start\" | \"center\" | \"end\"",
                        description: t("tablePage.cellAlign"),
                        name: "align"
                    },
                    {
                        description: t("tablePage.cellColSpan"),
                        defaultValue: "1",
                        name: "colSpan",
                        type: "number"
                    },
                    {
                        description: t("tablePage.cellIsShrink"),
                        defaultValue: "false",
                        name: "isShrink",
                        type: "boolean"
                    },
                    {
                        description: t("tablePage.cellIsSticky"),
                        name: "isSticky",
                        type: "boolean"
                    },
                    {
                        description: t("tablePage.cellStickyOffset"),
                        name: "stickyOffset",
                        defaultValue: "0",
                        type: "number"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("tablePage.cellCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("tablePage.headSortDirection"),
                        type: "\"ascending\" | \"descending\"",
                        name: "sortDirection"
                    },
                    {
                        description: t("tablePage.headScope"),
                        type: "\"col\" | \"row\"",
                        defaultValue: "\"col\"",
                        name: "scope"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("tablePage.partsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default TablePage;
